// Plays the app's audio.
//
// The app encodes with FFmpeg's AAC encoder (StreamBGFX.cpp, AudioCodecParams
// defaults: AAC-LC, 48kHz, stereo) and sends raw access units: no ADTS headers,
// and the encoder's extradata is never sent. WebCodecs reads raw AAC only with an
// AudioSpecificConfig as the description, so it is rebuilt here from the params —
// the same thing the native client gets away with by letting FFmpeg guess.
//
// Decoded frames are scheduled back to back on a WebAudio clock. Audio arrives in
// bursts (riding on video packets), so a small lead absorbs the jitter; if the
// queue drifts further ahead than MAX_LEAD it is dropped and restarted rather than
// letting audio lag the picture.
//
// Sound is off until the user turns it on with the topbar button, and that click
// is also what browser autoplay policy wants before an AudioContext may run. The
// choice is remembered, but whenever the browser still blocks playback the button
// shows sound off, since one click on it is exactly what is missing. Muting
// suspends the one page-wide context, which also stops the decoding work.

const AV_CODEC_ID_AAC = 0x15002;
const AAC_FRAME_SAMPLES = 1024;
const AAC_RATES = [
	96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350,
];
const LEAD = 0.08; // seconds
const MAX_LEAD = 0.4;
const STORAGE_KEY = "sound";

/** Fired on document with detail {on}: whether sound is actually playing. */
export const EventSoundChanged = "b6-sound-changed";
/** Fired on document when a stream turns out to carry audio. */
export const EventAudioAvailable = "b6-audio-available";

let ctx = null;
let wanted = false;
try {
	wanted = localStorage.getItem(STORAGE_KEY) === "on";
} catch {
	// Storage can be blocked; sound just starts off.
}

export function soundOn() {
	return ctx?.state === "running";
}

/** Called from the button's click, so resume() runs with the user gesture. */
export function setSound(on) {
	wanted = on;
	try {
		localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
	} catch {
		// Not remembered, still applied.
	}
	if (on) context().resume();
	else ctx?.suspend();
}

function context() {
	if (!ctx) {
		ctx = new AudioContext({latencyHint: "interactive"});
		ctx.onstatechange = () =>
			document.dispatchEvent(new CustomEvent(EventSoundChanged, {detail: {on: soundOn()}}));
		// Created outside a gesture it may start running anyway (Chrome, once the page
		// has been interacted with), so an unwanted one is put to sleep explicitly.
		if (!wanted) ctx.suspend();
	}
	return ctx;
}

/** AudioSpecificConfig for AAC-LC (ISO 14496-3 1.6.2.1). */
export function aacConfig(sampleRate, channels) {
	const rate = AAC_RATES.indexOf(sampleRate);
	if (rate < 0) return null;
	// 5 bits object type (2 = LC), 4 bits rate index, 4 bits channel config, 3 zero bits.
	return new Uint8Array([(2 << 3) | (rate >> 1), ((rate & 1) << 7) | (channels << 3)]);
}

export class AudioPlayer {
	#decoder = null;
	#paramsKey = "";
	#playhead = 0;
	#timestamp = 0;
	#announced = false;

	/**
	 * @param {{codecId: number, channels: number, sampleRate: number}|null} params
	 * @param {Uint8Array[]} frames
	 */
	push(params, frames) {
		if (typeof AudioDecoder === "undefined" || !params || frames.length === 0) return;
		if (params.codecId !== AV_CODEC_ID_AAC) return;
		if (!this.#announced) {
			this.#announced = true;
			document.dispatchEvent(new Event(EventAudioAvailable));
		}
		// Muted or blocked: nothing to decode for. AAC frames stand alone, so
		// skipping some costs nothing when sound comes back.
		if (wanted && !ctx) context();
		if (!soundOn()) return;
		if (!this.#ensureDecoder(params)) return;
		const frameUs = (AAC_FRAME_SAMPLES * 1e6) / params.sampleRate;
		for (const data of frames) {
			// EncodedAudioChunk copies, so the caller may transfer the buffer after.
			this.#decoder.decode(new EncodedAudioChunk({type: "key", timestamp: this.#timestamp, data}));
			this.#timestamp += frameUs;
		}
	}

	close() {
		if (this.#decoder && this.#decoder.state !== "closed") this.#decoder.close();
		this.#decoder = null;
	}

	#ensureDecoder(params) {
		const key = `${params.sampleRate}/${params.channels}`;
		if (this.#decoder && this.#decoder.state !== "closed" && key === this.#paramsKey) return true;
		if (this.#decoder && this.#decoder.state !== "closed") this.#decoder.close();
		this.#paramsKey = key;
		const description = aacConfig(params.sampleRate, params.channels);
		if (!description) return false;
		this.#decoder = new AudioDecoder({
			output: (data) => this.#play(data),
			// A bad frame closes the decoder; the next push builds a fresh one.
			error: (e) => console.error("audio decoder error", e),
		});
		this.#decoder.configure({
			codec: "mp4a.40.2",
			sampleRate: params.sampleRate,
			numberOfChannels: params.channels,
			description,
		});
		return true;
	}

	/** @param {AudioData} data */
	#play(data) {
		// While suspended the clock stands still, so scheduling would only pile up.
		if (!soundOn()) {
			data.close();
			return;
		}
		const buffer = ctx.createBuffer(data.numberOfChannels, data.numberOfFrames, data.sampleRate);
		for (let ch = 0; ch < data.numberOfChannels; ch++) {
			data.copyTo(buffer.getChannelData(ch), {planeIndex: ch, format: "f32-planar"});
		}
		data.close();

		const now = ctx.currentTime;
		if (this.#playhead < now || this.#playhead > now + MAX_LEAD) this.#playhead = now + LEAD;
		const source = ctx.createBufferSource();
		source.buffer = buffer;
		source.connect(ctx.destination);
		source.start(this.#playhead);
		this.#playhead += buffer.duration;
	}
}
